import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { CaptureRow, PhotoClass } from '../../data/database.types';
import type { CapturePhotoView } from '../../data/photos/photo-view';
import { useCapturePhotoBoard } from '../photos/use-capture-photo-board';
import { PhotoViewer } from '../photos/PhotoViewer';
import { formatCaptureTitle } from '../captures/capture-status';
import { useReportBuilder } from './report-builder-state';
import { REPORT_VIEWER_STATE_KEY, viewerPhotoKey } from './report-route';
import { ReportPhotoTile } from './ReportPhotoTile';
import { describeNoPhotos, viewerLabel } from './report-wording';

/**
 * ONE SELECTED CAPTURE'S EVIDENCE, INSIDE THE REPORT BUILDER — M10 / S10-02.
 *
 * **THE PHOTO PIPELINE IS REUSED, NOT REBUILT.** This mounts the same
 * `useCapturePhotoBoard` the Capture screen uses, so picking, processing,
 * staging, delivery, attachment, retry and the refusal wording are all the ones
 * already proven in M5. **A second upload path is not merely avoided, it is
 * impossible to add unnoticed**: `tests/unit/photo-upload-boundary.test.ts`
 * scans the source tree and fails if any module outside the delivery queue
 * uploads, reads Storage or calls the attach writer.
 *
 * **ONE BOARD PER SELECTED CAPTURE, WHICH IS ONE READ EACH.** That is accepted
 * for now rather than optimised away: a batched read would be a second
 * description of "which photos does this Capture have", and the two would drift.
 * If real reports turn out to cover many Captures at once, that is the moment
 * to measure it.
 *
 * **WHAT THIS SCREEN CANNOT DO.** It renders no Remove and no Before/After
 * change. Choosing a photo for a report is a temporary choice about ONE report;
 * it must never remove evidence, hide it, reclassify it or write Capture
 * history. The controls that can do those things live on the Capture screen,
 * and that screen is deliberately not reused here.
 */
export function ReportCapturePhotoGroup({
  capture,
  locationDisplay,
}: {
  readonly capture: CaptureRow;
  readonly locationDisplay: string | null;
}) {
  const builder = useReportBuilder();
  const board = useCapturePhotoBoard(capture.id);
  const location = useLocation();
  const navigate = useNavigate();

  const chosen = builder.photoKeysFor(capture.id);
  const baseline = builder.baselineFor(capture.id);
  const claimed = builder.autoSelectedFor(capture.id);

  /**
   * **AUTO-SELECTING A PHOTO BRANDON JUST ADDED — AND ONLY ONCE THE SERVER HAS
   * IT** (Jerry's decision 3, and his M-2 ruling).
   *
   * `addPhotos` returns nothing and the library path can add several photos at
   * once, so the only way to know which photos are new is to compare against
   * the keys that existed before the tap. That baseline lives in the builder,
   * not here, so it survives stepping back and forward.
   *
   * **`serverPhotoId` IS THE DEFINITION OF SUCCESS**, deliberately. It is null
   * until the server accepts the photo, so a pick that was cancelled, a file
   * the pipeline refused, or an upload that failed **can never produce a
   * selected photo that does not exist.** A retry that succeeds later simply
   * satisfies the same condition, and the photo is selected then — which is why
   * this reads the current state rather than reacting to the add call.
   *
   * **THIS EFFECT WATCHES AN EXTERNAL SYSTEM.** Delivery happens in a durable
   * queue outside React; attachment is news arriving from the server, not a
   * value this component computed. It calls the builder's own callback rather
   * than setting state here, so nothing cascades.
   */
  const attachedNewKeys = baseline
    ? board.views
        .filter((view) => !baseline.has(view.key))
        .filter((view) => view.serverPhotoId !== null)
        .map((view) => view.key)
        // Already claimed once, so the effect does not churn while the board
        // refreshes. **It is not the guard that protects a deselection** —
        // that is the builder's own, which survives this component
        // re-mounting when Brandon steps back and forward.
        .filter((key) => !claimed.has(key))
    : [];
  const pendingSignature = attachedNewKeys.join(',');

  useEffect(() => {
    if (pendingSignature === '') return;
    builder.autoSelectAttached(capture.id, pendingSignature.split(','));
  }, [pendingSignature, builder, capture.id]);

  const openViewer = (key: string): void => {
    // A HISTORY ENTRY, NOT A VARIABLE. Android's Back consumes this entry and
    // closes the photo, leaving Brandon on this step with every choice intact.
    void navigate(location.pathname, {
      state: { ...(location.state as object | null), [REPORT_VIEWER_STATE_KEY]: key },
    });
  };

  const openKey = viewerPhotoKey(location.state);
  const viewed = openKey ? (board.views.find((view) => view.key === openKey) ?? null) : null;

  const group = (photoClass: PhotoClass): CapturePhotoView[] =>
    board.views.filter((view) => view.photoClass === photoClass);

  return (
    <section className="report-photo-group" aria-labelledby={`report-capture-${capture.id}`}>
      <h2 id={`report-capture-${capture.id}`} className="report-photo-capture">
        {formatCaptureTitle(capture.capture_number, locationDisplay)}
      </h2>

      {board.loadError ? (
        <p className="cl-error" role="alert">
          {board.loadError}
        </p>
      ) : null}

      {/* Refusals and failures come from the pipeline's own wording, not from a
          second vocabulary invented here. */}
      {board.notices.length > 0 ? (
        <div className="capture-photos-notices" role="alert">
          {board.notices.map((notice) => (
            <p key={notice}>{notice}</p>
          ))}
          <button type="button" className="capture-photos-dismiss" onClick={board.dismissNotices}>
            Dismiss
          </button>
        </div>
      ) : null}

      {(['before', 'after'] as const).map((photoClass) => {
        const views = group(photoClass);
        return (
          <div key={photoClass} className="report-photo-class">
            <p className="report-photo-class-label">
              {photoClass === 'before' ? 'BEFORE' : 'AFTER'}
            </p>

            {/* HONEST ABOUT WHAT IS NOT THERE. A Capture with no Before photos
                says so; it does not render an empty row that reads as broken,
                and it never implies evidence exists that does not. */}
            {views.length === 0 ? (
              <p className="projects-empty-body report-photo-empty">
                {describeNoPhotos(photoClass)}
              </p>
            ) : (
              <ul className="report-photo-tiles">
                {views.map((view) => (
                  <ReportPhotoTile
                    key={view.key}
                    view={view}
                    selected={chosen.has(view.key)}
                    onToggle={() => {
                      builder.togglePhoto(capture.id, view.key);
                    }}
                    onInspect={() => {
                      openViewer(view.key);
                    }}
                  />
                ))}
              </ul>
            )}

            {/* **THE BUTTON IS THE CLASSIFICATION.** `addPhotos` will not
                compile without a Before-or-After argument, so a silent default
                is impossible — and nothing here infers one from status, from
                the picture, or from anything else (PT §9). */}
            <div className="report-photo-add">
              <button
                type="button"
                className="cl-button cl-button--quiet"
                disabled={board.adding}
                onClick={() => {
                  builder.beginAdd(
                    capture.id,
                    board.views.map((view) => view.key),
                  );
                  void board.addPhotos('camera', photoClass);
                }}
              >
                {board.adding
                  ? 'Adding…'
                  : `+ Take ${photoClass === 'before' ? 'Before' : 'After'}`}
              </button>
              <button
                type="button"
                className="cl-button cl-button--quiet"
                disabled={board.adding}
                onClick={() => {
                  builder.beginAdd(
                    capture.id,
                    board.views.map((view) => view.key),
                  );
                  void board.addPhotos('library', photoClass);
                }}
              >
                {board.adding
                  ? 'Adding…'
                  : `+ Choose ${photoClass === 'before' ? 'Before' : 'After'}`}
              </button>
            </div>
          </div>
        );
      })}

      {/* The existing viewer, reused unchanged. It carries NO Remove and NO
          Before/After control, so nothing about it can alter evidence. */}
      {viewed ? (
        <PhotoViewer
          view={viewed}
          label={viewerLabel(viewed)}
          onClose={() => {
            // Back through the entry the viewer occupies, so an explicit Close
            // and Android's Back button do exactly the same thing.
            void navigate(-1);
          }}
        />
      ) : null}
    </section>
  );
}
